/**
 * 账号注销（**N 域 N4 · 「删除账户（注销）」**）—— 契约与**唯一口径常量**
 *
 * 依据：N 域 PRD **§5.4.3**（语义 · 确认强度 · 冷静期 · 前置校验 · 双端可完成 · 状态可见）·
 * **§6.2 `N-O6`–`N-O8`**（冷静期内登录 / 撤销 / owner 仍有成员）· **§6.3 `N-C3`**（双端同时发起 ⇒ 幂等）·
 * **§7.4 `AC-N4.23`–`N4.31`** · **§6.4 铁律 9**（🔴 不得实现"解散账本"/"移除成员"语义 —— 那两者在 L 域）。
 *
 * ## 🔴 三分法（本域最容易被做错的一件事）
 *
 * | 动作 | 归属 | 语义 |
 * |---|---|---|
 * | **删除账户（注销）** | **本域** | 删「**人**」—— 账号 + 其数据 |
 * | **解散账本** | L 域（`POST /api/ledgers/dissolve`） | 「**分家**」—— 一本账按归属拆开 |
 * | **移除成员** | L 域 | 「一次**单人拆分**」 |
 *
 * ⇒ 本域**只做第一个**；遇到需要后两者的情况（如 owner 仍有成员）**拦截 + 引导**，**绝不代做**。
 *
 * ## 🔴 冷静期是全项目**唯一**的一个
 *
 * `DR-018` 已把它从 L 域**移出、专给本域** ⇒ 常量只有这一处（禁在端侧或服务端各写一个 `7`）。
 */

import { z } from 'zod';

/**
 * 冷静期天数 —— 🔴 **全项目唯一冷静期**（`AC-N4.25`：7 天，**不可缩短**）。
 *
 * "不可缩短"的含义：没有任何接口/参数能让它变短（本常量**不是**可配置项，也不进 env）。
 */
export const DELETION_COOLING_DAYS = 7;

/** 冷静期毫秒（由天数派生 —— 禁第二处 `7 * 24 * 3600 * 1000`） */
export const DELETION_COOLING_MS = DELETION_COOLING_DAYS * 24 * 60 * 60 * 1000;

/** 一天毫秒（仅用于"剩余天数"换算） */
const DAY_MS = 24 * 60 * 60 * 1000;

/** 注销态视图（`GET /api/settings/deletion`） */
export interface AccountDeletionView {
  /** `active` = 未申请（或已撤销）；`pending_deletion` = 冷静期中（账号冻结） */
  status: 'active' | 'pending_deletion';
  /** 冷静期截止（UTC 毫秒）；未申请 ⇒ `null` */
  deadlineAt: number | null;
  /**
   * 剩余**整天**（`AC-N4.31`：冷静期剩余天数双端可见）。
   *
   * 🔴 **只看这个字段** —— 端侧不自己算（"还剩几天"是**用户能拿去投诉**的数字，
   * 两端各算一次就可能差一天 ⇒ 由本文件的 {@link deletionDaysLeft} 唯一实现）。
   */
  daysLeft: number | null;
}

/** 申请注销的请求体 */
export interface RequestDeletionRequest {
  /**
   * 🔴 **二次输入 = 账号名**（原型第 2 步「输入账号名确认（与解散/移除同强度）」）。
   * 与 L 域「解散」输账本名、N4「overwrite 恢复」输账本名**同一强度**。
   */
  confirmName: string;
}

/**
 * 申请注销的 Zod 契约（服务端经 `nestjs-zod` 管道校验 —— 端侧**不写第二份** `length > 0`）。
 *
 * ⚠️ 长度上限**故意放宽到 50**：它只是"防呆"（拦住空串与明显异常），真正的判据是
 * **服务端比对账号名**（`confirmName !== user.name` ⇒ 400）—— 在契约里写死 `30` 会与
 * `users.name` 的实际约束形成第二份口径。
 */
export const RequestDeletionRequestSchema = z.object({
  confirmName: z.string().min(1).max(50),
});

/** 撤销后的响应与查询同形（端侧不必分两套处理） */
export type CancelDeletionResponse = AccountDeletionView;

/**
 * 冷静期剩余**整天**（**唯一实现** · 纯函数 · 零 I/O）。
 *
 * - 已过期（`deadlineAt <= now`）⇒ `0`（**不返回负数** —— 负数在 UI 上会变成"还剩 -1 天"）；
 * - 不足一天按 **1 天**（向上取整）：剩 3 小时显示"还剩 1 天"比"还剩 0 天"更符合直觉
 *   （后者读起来像"已经没了"，而账号其实还活着）。
 */
export function deletionDaysLeft(deadlineAt: number, now: number): number {
  const ms = deadlineAt - now;
  if (ms <= 0) return 0;
  // 🔴 **这不是金额**：`ms` 是**时长**、`DAY_MS` 是常数 ⇒ 上取整得到"剩余整天"。
  //    本仓 ESLint 禁 `Math.ceil` 与 `/`（防金额运算绕开定点白名单）⇒ 按提示显式豁免并写明理由。
  // eslint-disable-next-line no-restricted-syntax
  return Math.ceil(ms / DAY_MS);
}

/** 把「状态 + 截止时刻」归一成视图（服务端与端侧**同一口径**） */
export function deletionViewOf(
  status: AccountDeletionView['status'],
  deadlineAt: number | null,
  now: number,
): AccountDeletionView {
  if (status !== 'pending_deletion' || deadlineAt === null) {
    return { status, deadlineAt: null, daysLeft: null };
  }
  return { status, deadlineAt, daysLeft: deletionDaysLeft(deadlineAt, now) };
}
